import { createMuiTheme, colors } from "@material-ui/core";
import shadows from "./shadows";
import shadowswcag from "./shadowswcag";
import typography from "./typography";
import typographywcag from "./typographywcag";
import { plPL } from "@material-ui/core/locale";

const createKmTheme = (hiContrast, largeFont) => {
  return createMuiTheme(
    {
      hiContrast: hiContrast,
      largeFont: largeFont,
      palette: {
        common: hiContrast
          ? {
              black: "#FFFF00",
              white: "#000000",
              cookieBackground: "#000000",
              cookieButton: "#FFFF00",
              cookieBorder: "#FFFFFF",
              tableHighlighted: "rgba(255, 255, 255, 0.22)",
            }
          : {
              cookieBackground: "#2B373B",
              cookieButton: "#3174ad",
              cookieBorder: "#2B373B",
              tableHighlighted: "#fff9c4",
            },
        background: {
          dark: hiContrast ? "#000000" : "#F4F6F8",
          default: hiContrast ? "#000000" : colors.common.white,
          paper: hiContrast ? "#000000" : colors.common.white,
        },
        primary: hiContrast
          ? {
              main: "#FFFF00",
              light: "#FFFF00",
              dark: "#FFFF00",
              contrastText: "#000000",
            }
          : {
              main: "#14416a",
            },
        secondary: hiContrast
          ? {
              main: "#FFFF00",
              light: "#FFFF00",
              dark: "#FFFF00",
              contrastText: "#000000",
            }
          : {
              main: "#b2bbcb",
            },
        grey: hiContrast
          ? {
              "50": "#FFFF00",
              "100": "#FFFF00",
              "200": "#FFFF00",
              "300": "#FFFF00",
              "400": "#FFFF00",
              "500": "#FFFF00",
              "600": "#FFFF00",
              "700": "#FFFF00",
              "800": "#FFFF00",
              "900": "#FFFF00",
              A100: "#FFFF00",
              A200: "#FFFF00",
              A400: "#FFFF00",
              A700: "#FFFF00",
            }
          : {},
        text: hiContrast
          ? {
              primary: "#FFFF00",
              secondary: "#FFFFFF",
              disabled: "rgba(255, 255, 255, 0.5)",
              hint: "#FFFFFF",
              icon: "#FFFFFF",
            }
          : {
              primary: colors.blueGrey[900],
              secondary: colors.blueGrey[600],
            },
        error: hiContrast
          ? {
              light: "#FF0000",
              main: "#FF0000",
              dark: "#FF0000",
              contrastText: "rgba(0, 0, 0, 1)",
            }
          : {
              main: "#D0180B",
            },
        warning: hiContrast
          ? {
              light: "#FFFF00",
              main: "#000000",
              dark: "#FFFF00",
              contrastText: "rgba(0, 0, 0, 1)",
            }
          : { main: "#ff9800" },
        success: hiContrast
          ? {
              light: "#FFFF00",
              main: "#000000",
              dark: "#FFFF00",
              contrastText: "rgba(0, 0, 0, 1)",
            }
          : {
              main: "#338037",
            },
        info: hiContrast
          ? {
              light: "#FFFF00",
              main: "#000000",
              dark: "#FFFF00",
              contrastText: "rgba(0, 0, 0, 1)",
            }
          : { main: "#2196f3" },
        inlineloader: {
          main: "#1976d2",
        },
        divider: hiContrast ? "rgba(255, 255, 0, 0.56)" : "rgba(0, 0, 0, 0.12)",
        action: hiContrast
          ? {
              active: "#fff",
              hover: "rgba(255, 255, 255, 0.16)",
              selected: "rgba(255, 255, 255, 0.22)",
              disabled: "rgba(255, 255, 255, 0.3)",
              disabledBackground: "rgba(255, 255, 255, 0.12)",
              disabledOpacity: 1,
              focus: "rgba(0, 0, 0, 0.12)",
              focusOpacity: 0.25,
              activatedOpacity: 0.25,
            }
          : {},
        contrastThreshold: 4.5,
      },
      shadows: hiContrast ? shadowswcag : shadows,
      typography: largeFont ? typographywcag : typography,
      overrides: {
        MuiFormLabel: largeFont
          ? { fontSize: "1.8rem" }
          : {
              root: {
                fontSize: "1.2rem",
                paddingBottom: "0.2rem",
              },
            },
        MuiTableCell: {
          head: {
            fontWeight: 501,
          },
          root: {
            paddingLeft: 7,
            paddingRight: 7,
            paddingTop: 7,
            paddingBottom: 7,
          },
        },
        MuiDropzoneArea: {
          root: {
            minHeight: 60,
          },
        },
        MuiContainer: {
          root: {
            paddingLeft: 4,
            paddingRight: 4,
            borderColor: "#FFFF00",
          },
        },
        MuiOutlinedInput: hiContrast
          ? {
              notchedOutline: {
                borderColor: "#FFFF00",
              },
            }
          : {},
        MuiTab: hiContrast
          ? {
              textColorPrimary: {
                color: "#000000",
                selected: "#000000",
              },
            }
          : {},
        Mui: hiContrast
          ? {
              root: {
                "&$selected": {
                  color: "#000000",
                },
              },
            }
          : {},
        PrivateTabIndicator: hiContrast
          ? {
              colorPrimary: {
                backgroundColor: "#000000",
              },
            }
          : {},
        MuiAlert: hiContrast
          ? {
              filledInfo: {
                color: "#000000",
                backgroundColor: "#FFFFFF",
              },
              filledSuccess: {
                color: "#000000",
                backgroundColor: "#FFFFFF",
              },
              standardSuccess: {
                color: "#000000",
                backgroundColor: "#FFFF00",
              },
              standardError: {
                color: "#000000",
                backgroundColor: "#FFFF00",
              },
              standardWarning: {
                color: "#000000",
                backgroundColor: "#FFFF00",
              },
            }
          : {},
        MuiButton: largeFont
          ? {
              root: {
                fontSize: "1.3125rem",
              },
              containedSizeLarge: {
                fontSize: "1.407rem",
              },
              containedSizeSmall: {
                fontSize: "1.21875rem",
              },
              textSizeSmall: {
                fontSize: "1.21875rem",
              },
              iconSizeMedium: {
                fontSize: 30,
              },
            }
          : {},
        MuiIconButton: hiContrast
          ? {
              colorSecondary: {
                color: "#000000",
              },
            }
          : {},
        MuiSvgIcon: largeFont
          ? { root: { fontSize: "1.5rem", width: "1.5em", height: "1.5em" } }
          : {},
        MuiTab: largeFont ? { root: { fontSize: "1.3125rem" } } : {},
        MuiTablePagination: largeFont ? { input: { fontSize: "1.5rem" } } : {},
        MuiButtonBase: hiContrast
          ? {
              root: {
                "&:focus": {
                  borderBottom: "2px solid #FFFF00",
                },
              },
            }
          : {
              root: {
                "&:focus": {
                  borderBottom: "2px solid black",
                },
              },
            },
        MuiLink: hiContrast
          ? {
              root: {
                "&:focus": {
                  borderBottom: "2px solid #FFFF00",
                  padding: 2,
                },
              },
              underlineHover: {
                textDecoration: "underline",
              },
            }
          : {
              root: {
                "&:focus": {
                  borderBottom: "2px solid black",
                  padding: 2,
                },
              },
            },
        MuiIconButton: {
          root: {
            "&:focus": {
              borderBottom: "2px solid black",
            },
          },
        },
        MuiSwitch: {
          "&:focus": {
            borderBottom: "2px solid black",
          },
        },
        MuiInput: hiContrast
          ? {
              underline: {
                "&::before": {
                  borderBottom: "2px solid rgba(255, 255, 0, 0.52)",
                },
              },
            }
          : {},
        MuiFilledInput: hiContrast
          ? {
              root: {
                backgroundColor: "rgba(255, 255, 0, 0.13)",
              },
              underline: {
                "&::before": {
                  borderBottom: "2px solid rgba(255, 255, 0, 0.52)",
                },
              },
            }
          : {}
      },
      dimensionscustom: largeFont
        ? {
            menuWidth: 280,
          }
        : {
            menuWidth: 256,
          },
    },
    plPL
  );
};

const getGlobalTheme = (hiContrast, largeFont) => {
  return createKmTheme(hiContrast, largeFont);
};

export { getGlobalTheme };
